<template>
  <div id="unitsForm">
    <v-overlay :value="loading" z-index="6">
      <v-progress-circular indeterminate size="64"/>
    </v-overlay>
    <v-row no-gutters justify="start">
      <v-col cols="9">
        <v-card class="pa-3">
          <v-card-title>
            <h4>{{$t($route.meta.breadcrumb[2].text)}}</h4>
          </v-card-title>
          <v-card-subtitle>
            <p>{{$t('units.formMessage')}}</p>
          </v-card-subtitle>
          <v-card-text>
            <v-progress-linear v-if="isEdit && loadItem" color="primary" indeterminate/>
            <p v-if="isEdit && loadItem" class="text-center">{{$t('general.loading')}}</p>
            <v-form lazy-validation ref="form" v-model="valid" v-if="!loadItem || !isEdit">
              <v-row>
                <v-col cols="6">
                  <v-text-field
                    outlined
                    dense
                    :label="'VIN'"
                    :rules="rules.vin"
                    maxlength="17"
                    v-model="editedItem.vin_number"
                  />
                </v-col>
                <v-col cols="6">
                  <v-autocomplete
                    outlined
                    dense
                    :rules="rules.required"
                    :items="unitTypes"
                    item-text="alias"
                    item-value="id"
                    v-model="editedItem.unit_type_id"
                  >
                    <template v-slot:label>
                      <span>{{$t('general.unitType')}}</span><span class="error--text">&nbsp;*</span>
                    </template>
                  </v-autocomplete>
                </v-col>
                <v-col cols="6">
                  <v-autocomplete
                    outlined
                    dense
                    :items="siteCapitalizeWords"
                    item-text="name"
                    item-value="id"
                    :rules="rules.required"
                    v-model="editedItem.site_id"
                  >
                    <template v-slot:label>
                      <span>{{$t('general.site')}}</span><span class="error--text">&nbsp;*</span>
                    </template>
                  </v-autocomplete>
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    outlined
                    dense
                    :label="$t('general.unitNumber')"
                    :rules="rules.unitNumber"
                    v-model="editedItem.unit_number"
                  />
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    outlined
                    dense
                    :label="$t('general.brand')"
                    :rules="rules.textNoReq"
                    v-model="editedItem.brand"
                  />
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    outlined
                    dense
                    :label="$t('general.model')"
                    :rules="rules.textNoReq"
                    v-model="editedItem.model"
                  />
                </v-col>
                <template v-if="userLoggedCompanies === 14">
                  <v-col cols="6">
                    <v-text-field
                      outlined
                      dense
                      :label="$t('general.mechanicalCheck')"
                      :rules="rules.maxText"
                      v-model="editedItem.mechanical_check"
                    />
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      outlined
                      dense
                      :rules="rules.textNoSpecialChars"
                      :label="$t('general.smokeCheck')"
                      v-model="editedItem.smoke_check"
                    />
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      outlined
                      dense
                      :label="$t('general.certification')"
                      :rules="rules.maxText"
                      v-model="editedItem.certificate"
                    />
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      outlined
                      dense
                      :label="$t('general.wetMeasurement')"
                      :rules="rules.specificDecimal"
                      v-model="editedItem.wet_measure"
                    />
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      outlined
                      dense
                      :label="$t('general.dryMeasurement')"
                      :rules="rules.specificDecimal"
                      v-model="editedItem.dry_measure"
                    />
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      outlined
                      dense
                      :label="$t('general.capacity')"
                      :rules="rules.textNoReq"
                      v-model="editedItem.capacity"
                    />
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      outlined
                      dense
                      label="Kit"
                      :rules="rules.textNoReq"
                      v-model="editedItem.kit"
                    />
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      outlined
                      dense
                      :label="$t('general.typeOperation')"
                      :rules="rules.textNoReq"
                      v-model="editedItem.operation_type"
                    />
                  </v-col>
                </template>
                <v-col class="pt-0" cols="6">
                  <span>{{$t('general.property')}}</span><span class="error--text">&nbsp;*</span>
                  <v-radio-group :rules="rules.required" class="mt-0" row v-model="editedItem.owner">
                    <v-radio
                      v-for="item in property_type"
                      :key="item.id"
                      :label="item.name"
                      :value="item.value"
                    />
                  </v-radio-group>
                  <v-text-field
                    v-if="editedItem.owner === 'EXTERNAL'"
                    outlined
                    dense
                    :label="$t('general.ownerName')"
                    v-model="editedItem.owner_name"
                    :rules="rules.textRules"/>
                </v-col>
                <v-col class="pb-0" cols="12">
                  <v-textarea
                    outlined
                    dense
                    rows="2"
                    :label="$t('general.comment')"
                    :rules="rules.textNoReq"
                    v-model="editedItem.comment"
                  />
                </v-col>
                <v-col class="pt-0" cols="12">
                  <h3 class="mb-2">{{$t('general.plates')}}</h3>
                  <v-divider class="my-0"/>
                </v-col>
                <v-col v-for="(plate, index) in editedItem.plates" :key="index" cols="12">
                  <v-card outlined>
                    <v-card-text>
                      <v-row>
                        <v-col class="py-0" cols="12">
                          <span class="font-weight-bold"> {{$t('general.plate') + ' ' + (index+1)}}:</span>
                        </v-col>
                        <v-col>
                          <v-text-field
                            outlined
                            dense
                            hide-details="auto"
                            v-model="plate.plate_number"
                            :rules="rules.plate"
                          >
                            <template v-slot:label>
                              <span>{{$t('address.number')}}</span><span class="error--text">&nbsp;*</span>
                            </template>
                          </v-text-field>
                        </v-col>
                        <v-col>
                          <v-autocomplete
                            outlined
                            dense
                            hide-details="auto"
                            :items="countries"
                            item-text="name"
                            item-value="id"
                            v-model="plate.country_id"
                            :rules="rules.required"
                          >
                            <template v-slot:label>
                              <span>{{$t('address.country')}}</span><span class="error--text">&nbsp;*</span>
                            </template>
                          </v-autocomplete>
                        </v-col>
                        <v-col v-if="editedItem.plates.length > 1" cols="2" class="text-center">
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                              <v-icon
                                class="mx-2"
                                @click="removePlate(plate, index)"
                                v-on="on"
                              >far fa-trash-alt</v-icon>
                            </template>
                            <span>{{$t('units.removePlate')}}</span>
                          </v-tooltip>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col class="pt-0" cols="12">
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-btn height="50" depressed block @click="addPlate" v-on="on"><v-icon color="grey">fas fa-plus</v-icon></v-btn>
                    </template>
                    <span>{{$t('units.addPlates')}}</span>
                  </v-tooltip>
                </v-col>
                <v-col class="pt-0" cols="12">
                  <v-divider class="my-0"/>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" class="text-right">
                  <v-btn
                    class="mr-4"
                    color="primary"
                    outlined
                    @click="redirect"
                  >{{$t('general.cancel')}}</v-btn>
                  <v-btn
                    color="primary"
                    depressed
                    @click="add"
                  >{{$t('general.save')}}</v-btn>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-dialog v-model="removeDialog" width="500px">
      <v-card>
        <v-toolbar color="grey lighten-5" flat class="mb-5">
          <h3 class="error--text">{{$t('general.confirm')}}</h3>
        </v-toolbar>
        <v-card-text>
          <p>{{$t('units.delPlateMessage')}}?</p>
          <p><span class="error--text font-weight-black">{{$t('general.plate')}}:</span> {{removedItem.plate_number}}</p>
          <p><span class="error--text font-weight-black">{{$t('address.country')}}:</span> {{removedItem.country_id}}</p>
        </v-card-text>
        <v-divider/>
        <v-card-actions>
          <v-spacer/>
          <v-btn color="error" text @click="close">{{$t('general.cancel')}}</v-btn>
          <v-btn :disabled="loadBtn" :loading="loadBtn" color="error" depressed @click="confirmRemove">{{$t('general.accept')}}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {mask} from 'vue-the-mask'
export default {
  name: 'unitsForm',
  directives: {mask},
  data () {
    return {
      loading: false,
      loadItem: true,
      loadBtn: false,
      removeDialog: false,
      valid: true,
      editedItem: {
        id: 0,
        brand: '',
        model: '',
        vin_number: '',
        unit_type_id: '',
        unit_number: '',
        site_id: '',
        owner: '',
        owner_name: '',
        comment: '',
        plates: [
          {
            plate_number: '',
            country_id: 150,
          }
        ],
        mechanical_check: '',
        smoke_check: '',
        certificate: '',
        wet_measure:  '',
        dry_measure: '',
        capacity:   '',
        kit: '',
        operation_type: ''
      },
      removedItem: {
        plate_number: '',
        country_id: ''
      },
      countries: [],
      unitTypes: [],
      sites: [],
      platesCounter: 0
    }
  },
  created () {
    if (this.$route.params.id) {
      let id = this.$route.params.id
      this.$store.dispatch('units/getById', {event: {id, context: this}})
    }
    if (this.role !== 2) {
      this.$store.dispatch('sites/getByUser', {event: {context: this}})
    } else {
      this.$store.dispatch('sites/get', {event: {item: {perPage: -1, page: 1}, context: this}})
    }
      this.$store.dispatch('unitTypes/getAll', {event: {context: this}})
      this.$store.dispatch('getCountry', {event: {context: this}})
  },
  watch: {
   'editedItem.owner' (val) {
    if (val !== 'EXTERNAL') {
      this.editedItem.owner_name = ''
    }
   }
  },
  computed: {
    isEdit () {
      if (this.$route.params.id) {
        return true
      } else {
        return false
      }
    },
    rules () {
      return this.$validationRules()
    },
    role () {
      if (this.$store.state.userLogged.roles) {
        return this.$store.state.userLogged.roles[0].id
      } else {
        return 0
      }
    },
    userLoggedCompanies() {
      if (this.$store.state.userLogged && this.$store.state.userLogged.companies) {
        return this.$store.state.userLogged.companies[0].id;
      } else {
        return null;
      }
    },
    property_type () {
      return [
        {id: 1, name: this.$t('units.external'), value: 'EXTERNAL'},
        {id: 2, name: this.$t('units.own'), value: 'OWN'},
      ]
    },
    siteCapitalizeWords(){
      if (this.sites.length === 0) return []

      return this.sites.map(site => {
        return {
          ...site,
          name: this.capitalizeWords(site.name)
        }
      })
    }
  },
  methods: {
    add () {
      if (this.$refs.form.validate()) {
        this.loading = true
        if (this.isEdit) {
          this.$store.dispatch('units/put', {event: {item: this.editedItem ,context: this}})
        } else if (!this.isEdit) {
          this.$store.dispatch('units/post', {event: {item: this.editedItem ,context: this}})
        }
      }
    },
    addPlate () {
      this.editedItem.plates.push({number: '', country: ''})
    },
    removePlate (item, index) {
      this.removedItem = Object.assign({}, item),
      this.removedItem.index = index
      this.removeDialog = true
    },
    redirect () {
      this.$router.push({name: 'Units'})
    },
    close () {
      this.removeDialog = false
      this.removedItem = {
        plate_number: '',
        country_id: ''
      }
    },
    confirmRemove () {
      // this.loadBtn = true // PENDIENTE YA QUE NO ESTA LA FUNCIÓN COMPLETA
      this.editedItem.plates.splice(this.removedItem.index, 1)
      this.close()
      // if (this.removedItem.id) {
      //   this.$store.dispatch('units/delPlate', {event: {id: this.removedItem.id, context: this}})
      // } else {
      //   this.editedItem.plates.splice(this.removedItem.index, 1)
      //   this.close()
      // }
    },
    getItem () {
      let id = this.$route.params.id
      this.$store.dispatch('units/getById', {event: {id, context: this}})
    },
    capitalizeWords(str) {
      if (!str) return ''
      
      return str
        .toLowerCase()
        .replace(/(^|\s)\p{L}/gu, c => c.toUpperCase());
    }
  }
}
</script>